// PEP Balancing — Guest Balancing and Group Master, ported from the desktop toolkit's PEPBalancing pages.
//
// EVERYTHING HERE RUNS IN THE BROWSER, as it did on the desktop, and nothing is uploaded: a PEP guest file carries guest
// names, e-mail addresses and card tokens, and the checks below need nothing a server has. The libraries load only
// when a PEP screen is opened (fast-xml-parser, and pdfjs-dist for the Opera guest-ledger PDF).
//
// The parsing and matching rules are the desktop's, unchanged.

import { loadPdfjs } from './pdfjs';

const loadXml = () => import('fast-xml-parser');

const arr = (v) => (v == null ? [] : (Array.isArray(v) ? v : [v]));

// ── Guest Balancing: the PEP guest XML (System21 › STAY › GUEST › FOLIO › FOLIOBILL › TRANS › TRAN) ───────────────

const GUEST_ARRAYS = ['STAY', 'FOLIOBILL', 'TRAN', 'ADDRESS', 'SPECIAL_REQ'];
const guestParserOptions = { ignoreAttributes: false, attributeNamePrefix: '@_', parseTagValue: true, isArray: (n) => GUEST_ARRAYS.includes(n) };

/**
 * One row per stay, each with its folio bills and their transactions. A bill is flagged:
 *   isHTS     — its card id is an HTS token (starts with H, longer than 10);
 *   isFakeExp — its card expiry is the 2099 placeholder;
 * and a stay `hasDupReceipts` when two of its bills share a receipt id.
 */
export const parsePepGuestXml = async (xmlText) => {
    const { XMLParser } = await loadXml();
    const doc = new XMLParser(guestParserOptions).parse(xmlText);
    const root = doc?.System21 ?? doc;
    const hotelInfo = {
        hotelCode: String(root?.Hotel_Code ?? ''), hotelName: String(root?.Hotel_Name ?? ''),
        businessDate: String(root?.Business_Date ?? ''), currency: String(root?.Currency_Code ?? ''),
    };
    const stays = arr(root?.STAY).map((s, stayIdx) => {
        const g = s?.GUEST ?? {};
        const bills = arr(g?.FOLIO?.FOLIOBILL).map((fb, fbIdx) => {
            const txns = arr(fb?.TRANS?.TRAN).map((t) => ({
                trans_id: String(t?.trans_id ?? ''), accounting_id: String(t?.accounting_id ?? ''),
                accounting_desc: String(t?.accounting_id_desc ?? ''), amount: parseFloat(t?.ledger_entry_amount ?? 0) || 0,
                entry_type: String(t?.entry_type ?? ''), bus_date: String(t?.bus_date ?? ''),
            }));
            const ccId = String(fb?.fb_credit_card_id ?? '').trim();
            const ccExp = String(fb?.fb_credit_card_exp_date ?? '').trim();
            return {
                fbIdx, key: `${stayIdx}-${fbIdx}`,
                receipt_id: String(fb?.receipt_id ?? ''), mop_type: String(fb?.fb_mop_type ?? ''),
                cc_type: String(fb?.fb_credit_card_type ?? ''), cc_id: ccId, cc_exp: ccExp,
                isHTS: ccId.toUpperCase().startsWith('H') && ccId.length > 10,
                isFakeExp: ccExp.includes('2099'),
                txns, txnTotal: txns.reduce((n, t) => n + t.amount, 0),
            };
        });
        const receiptCounts = {};
        bills.forEach((b) => { receiptCounts[b.receipt_id] = (receiptCounts[b.receipt_id] || 0) + 1; });
        return {
            stayIdx, stay_id: String(s?.stay_id ?? ''), confirmation_num: String(s?.confirmation_num ?? ''),
            stay_status: String(s?.stay_status ?? ''), arrival_date: String(s?.arrival_date ?? ''),
            departure_date: String(s?.departure_date ?? ''), room_type: String(s?.room_type ?? ''),
            rate_plan_id: String(s?.rate_plan_id ?? ''), group_code: String(s?.group_code ?? ''),
            lastname: String(g?.guest_lastname ?? ''), firstname: String(g?.guest_firstname ?? ''),
            email: String(g?.email_address ?? ''), bills,
            hasDupReceipts: Object.values(receiptCounts).some((c) => c > 1),
        };
    });
    return { hotelInfo, stays };
};

/** Every bill but the first of each receipt id, per stay — the desktop's "Remove all duplicates". */
export const duplicateBillKeys = (stays) => {
    const out = new Set();
    for (const s of stays) {
        if (!s.hasDupReceipts) continue;
        const seen = {};
        for (const b of s.bills) {
            if (seen[b.receipt_id]) out.add(b.key); else seen[b.receipt_id] = true;
        }
    }
    return out;
};

/** The original file with the bills in `deletedKeys` ("stayIdx-fbIdx") removed — the cleaned file to import. */
export const exportCleanPepXml = async (rawText, deletedKeys) => {
    if (!deletedKeys.size) return rawText;
    const { XMLParser, XMLBuilder } = await loadXml();
    const doc = new XMLParser(guestParserOptions).parse(rawText);
    const root = doc?.System21 ?? doc;
    arr(root?.STAY).forEach((s, sIdx) => {
        const folio = s?.GUEST?.FOLIO;
        if (!folio) return;
        folio.FOLIOBILL = arr(folio.FOLIOBILL).filter((_, fbIdx) => !deletedKeys.has(`${sIdx}-${fbIdx}`));
    });
    const xml = new XMLBuilder({ ignoreAttributes: false, attributeNamePrefix: '@_', format: true, indentBy: '  ', suppressEmptyNode: false }).build(doc);
    return `<?xml version="1.0" encoding="UTF-8"?>\n${xml}`;
};

/** Transactions rolled up by accounting id across the given stays, largest first. */
export const transactionRollup = (stays) => {
    const by = {};
    for (const s of stays) for (const b of s.bills) for (const t of b.txns) {
        const k = t.accounting_id || 'UNK';
        const r = (by[k] = by[k] || { id: k, desc: t.accounting_desc || t.accounting_id, count: 0, charges: 0, payments: 0, total: 0 });
        r.count++;
        r.total += t.amount;
        if (t.amount < 0) r.payments += t.amount; else r.charges += t.amount;
    }
    return Object.values(by).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
};

// ── Guest Balancing: the Opera naGuestLedgerDetail PDF ───────────────────────────────────────────────────────────

const pdfDate = (d) => { const m = d.match(/^(\d{2})-(\d{2})-(\d{2})$/); return m ? `${m[1]}/${m[2]}/20${m[3]}` : d; };
const pdfName = (raw) => {
    const parts = raw.split(',');
    return { last: (parts[0] ?? '').trim(), first: parts.slice(1).join(',').trim().replace(/,?\s*(Mr|Mrs|Ms|Mr\.|Mrs\.)\s*$/i, '').trim() };
};

/**
 * Rows of the Opera guest-ledger PDF: text items binned into lines by height, a data line being one that starts with a
 * 4-digit room number and carries two dates (MM-DD-YY). Header / footer lines are skipped — the pattern includes the
 * property-brand words the desktop's sample report carried.
 */
export const parseGuestLedgerPdf = async (arrayBuffer) => {
    const pdfjs = await loadPdfjs();   // PDF.js, loaded on first use (utils/pdfjs)
    const pdf = await pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
    const SKIP = /Guest Ledger|DoubleTree|Hilton|naguestleddetail|Room No|Arr\. Date|Passer By|Guest Total|Page \d/i;
    const DATE = /^\d{2}-\d{2}-\d{2}$/;
    const CC = /^(VS|MC|AX|CA|DS|CU|JC|CB|DC)$/;
    const num = (s) => parseFloat(String(s ?? 0).replace(/,/g, '')) || 0;
    const rows = [];
    for (let p = 1; p <= pdf.numPages; p++) {
        const content = await (await pdf.getPage(p)).getTextContent();
        const byY = {};
        for (const it of content.items) {
            const text = it.str?.trim();
            if (!text) continue;
            const y = Math.round(it.transform[5] / 3) * 3;
            (byY[y] = byY[y] || []).push({ x: it.transform[4], text });
        }
        for (const y of Object.keys(byY).map(Number).sort((a, b) => b - a)) {
            const items = byY[y].sort((a, b) => a.x - b.x);
            if (SKIP.test(items.map((i) => i.text).join(' '))) continue;
            if (!/^\d{4}$/.test(items[0]?.text)) continue;
            const dates = [];
            items.forEach((it, i) => { if (DATE.test(it.text)) dates.push(i); });
            if (dates.length < 2) continue;
            const rest = items.slice(dates[1] + 1);
            const merged = [];
            for (let i = 0; i < rest.length; i++) {
                if (rest[i].text === '-' && i + 1 < rest.length) { merged.push({ text: `-${rest[i + 1].text}` }); i++; } else merged.push(rest[i]);
            }
            const ccIdx = merged.findIndex((it) => CC.test(it.text));
            const n = ccIdx >= 0 ? merged.slice(0, ccIdx) : merged;
            const nameRaw = items.slice(1, dates[0]).map((i) => i.text).join(' ');
            rows.push({
                roomNo: items[0].text, nameRaw, ...pdfName(nameRaw),
                arrDate: pdfDate(items[dates[0]].text), depDate: pdfDate(items[dates[1]].text),
                persons: parseInt(n[0]?.text ?? '0', 10) || 0, roomCharges: num(n[1]?.text), otherCharges: num(n[2]?.text),
                credit: num(n[3]?.text), balance: num(n[4]?.text), rate: num(n[5]?.text),
                paymentMethod: ccIdx >= 0 ? merged[ccIdx].text : '',
            });
        }
    }
    return rows;
};

/** Guest-ledger rows ↔ PEP stays on last name + arrival + departure; each stay used once. */
export const matchGlToPep = (glRows, stays) => {
    const index = new Map();
    for (const s of stays) {
        const k = `${s.lastname.toLowerCase()}|${s.arrival_date}|${s.departure_date}`;
        (index.get(k) || index.set(k, []).get(k)).push(s);
    }
    const used = new Set();
    const matched = [];
    const glOnly = [];
    for (const gl of glRows) {
        const c = index.get(`${gl.last.toLowerCase()}|${gl.arrDate}|${gl.depDate}`) || [];
        const stay = c.find((s) => !used.has(s.stay_id)) || c[0] || null;
        if (stay) { used.add(stay.stay_id); matched.push({ gl, stay }); } else glOnly.push(gl);
    }
    return { matched, glOnly, pepOnly: stays.filter((s) => !used.has(s.stay_id)) };
};

// ── Group Master ─────────────────────────────────────────────────────────────────────────────────────────────────

/** PEP group masters (System21 › GROUPMASTER), with their member reservations counted. */
export const parsePepGroupXml = async (xmlText) => {
    const { XMLParser } = await loadXml();
    const doc = new XMLParser({ ignoreAttributes: false, parseTagValue: true }).parse(xmlText);
    return arr((doc?.System21 ?? doc)?.GROUPMASTER).map((g) => {
        const res = arr(g?.GROUP_MEMBERS?.Reservations?.Reservation);
        return {
            group_id: String(g.group_id ?? ''), group_code: String(g.group_code ?? '').trim(), group_name: String(g.group_name ?? '').trim(),
            group_begin_date: String(g.group_begin_date ?? '').trim(), group_end_date: String(g.group_end_date ?? '').trim(),
            definite_ind: String(g.definite_ind ?? ''), status: String(g.group_status_ind ?? '').trim(),
            rate_plan: String(g.special_rate_plan ?? '').trim(), rate_plan_desc: String(g.rate_plan_desc ?? '').trim(),
            reservation_count: res.filter((r) => r?.confirmation_num).length,
        };
    });
};

/** Opera v5 Block Info XML — the several wrappers the desktop recognised, and UPPER / lower field names. */
export const parseOperaBlockXml = async (xmlText) => {
    const { XMLParser } = await loadXml();
    const doc = new XMLParser({ ignoreAttributes: false, parseTagValue: true, attributeNamePrefix: '@_' }).parse(xmlText);
    const candidates = [
        doc?.BLOCKS?.BLOCK, doc?.Blocks?.Block, doc?.blocks?.block, doc?.REPORT?.BLOCK, doc?.Report?.Block,
        doc?.BLOCKINFO?.BLOCK, doc?.BlockInfo?.Block, doc?.DATA?.BLOCK, doc?.System21?.GROUPMASTER, doc?.System21?.BLOCK,
        ...Object.values(doc ?? {}).flatMap((v) => (typeof v === 'object' && v !== null ? Object.values(v) : [])),
    ].filter(Boolean);
    let rows = null;
    for (const c of candidates) {
        if (Array.isArray(c) && c.length) { rows = c; break; }
        if (typeof c === 'object' && !Array.isArray(c)) { rows = [c]; break; }
    }
    if (!rows) return [];
    const get = (o, ...keys) => {
        for (const k of keys) {
            const v = o[k] ?? o[k.toLowerCase()] ?? o[k.toUpperCase()];
            if (v !== undefined && v !== null) return String(v).trim();
        }
        return '';
    };
    return rows.map((b) => ({
        group_code: get(b, 'BLOCK_CODE', 'block_code', 'group_code', 'GROUP_CODE', 'RATE_CODE', 'rate_code'),
        group_name: get(b, 'BLOCK_NAME', 'block_name', 'group_name', 'GROUP_NAME', 'NAME', 'name'),
        group_begin_date: get(b, 'BEGIN_DATE', 'begin_date', 'ARRIVAL_DATE', 'arrival_date', 'group_begin_date', 'START_DATE'),
        group_end_date: get(b, 'END_DATE', 'end_date', 'DEPARTURE_DATE', 'departure_date', 'group_end_date'),
        status: get(b, 'BLOCK_STATUS', 'block_status', 'STATUS', 'status', 'group_status_ind', 'GROUP_STATUS_IND'),
        rate_plan: get(b, 'RATE_CODE', 'rate_code', 'SPECIAL_RATE_PLAN', 'special_rate_plan', 'RATE_PLAN', 'rate_plan'),
        rate_plan_desc: get(b, 'RATE_DESCRIPTION', 'rate_description', 'RATE_DESC', 'rate_plan_desc', 'RATE_PLAN_DESC'),
        reservation_count: Number(get(b, 'PICKUP', 'pickup', 'ROOMS_PICKED_UP', 'rooms_picked_up', 'RESERVATION_COUNT', 'reservation_count') || 0),
    }));
};

/** PEP groups ↔ Opera blocks by code (case-insensitive); a matched pair notes which of status, rate, dates, name differ. */
export const matchGroups = (pepGroups, operaBlocks) => {
    const opera = new Map(operaBlocks.filter((b) => b.group_code).map((b) => [b.group_code.toUpperCase(), b]));
    const matched = [];
    const pepOnly = [];
    for (const p of pepGroups) {
        const k = p.group_code.toUpperCase();
        const o = opera.get(k);
        if (!o) { pepOnly.push(p); continue; }
        const diffs = [];
        if (p.status && o.status && p.status !== o.status) diffs.push('Status');
        if (p.rate_plan && o.rate_plan && p.rate_plan.toUpperCase() !== o.rate_plan.toUpperCase()) diffs.push('Rate plan');
        if (p.group_begin_date && o.group_begin_date && p.group_begin_date !== o.group_begin_date) diffs.push('Begin date');
        if (p.group_end_date && o.group_end_date && p.group_end_date !== o.group_end_date) diffs.push('End date');
        if (p.group_name && o.group_name && p.group_name.toUpperCase() !== o.group_name.toUpperCase()) diffs.push('Name');
        matched.push({ pep: p, opera: o, diffs });
        opera.delete(k);
    }
    return { matched, pepOnly, operaOnly: [...opera.values()] };
};
